import React, {useState, useMemo, useEffect, startTransition } from 'react'
import { UserLookFormFilters, StyledDateRangePicker } from "./StyledComponents";
import {
  Input,
  DateRangePicker,
  Button,
} from 'cdk-radial';
import moment, { Moment } from 'moment';
import { MultiAutocomplete } from './MultiAutoComplete';
import { useIamUserProfileStore } from '../../Store';
import { useTranslation } from 'react-i18next';
import getCdkSidFromOkta from '../../util/getCdkSidFromOkta'
import { SPOKE_USERS } from '../../constants'
import {useAnalytics} from '../../v2/third-party-integration/amplitude';
import {AMPLITUDE_EVENTS} from '../../constants';

export interface UniPulseUrlParams {
  startDate: string;
  endDate: string;
  ip: string;
  storeids: string;
  loginid: string;
  groupType: string;
  enterpriseId: string;
}

export default function UserLookForm({ searchHandler }: {
      searchHandler: Function
    }) {   
    
    const cdksid = getCdkSidFromOkta();
    const defEndDate = moment.utc(new Date(),'YYYY-MM-DDThh:mm:ss A Z');
    const defStartDate = moment.utc(new Date(),'YYYY-MM-DDThh:mm:ss A Z').subtract(6, 'days');
    const [startDate, setStartDate] = useState(defStartDate);
    const [endDate, setEndDate] = useState(defEndDate);    
    const [loginId, setLoginId] = useState('');
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [ip, setIp] = useState('');
    const [rangeError, setRangeError] = useState(false);
    const iamUserInfo = useIamUserProfileStore((state:any) => state.iamUserInfo);
    const [selectedOption, setSelectedOption] = useState({ label: '' } as any);
    const {t} = useTranslation();
    const { trackEvent } = useAnalytics();

    const enterpriseId = iamUserInfo?.fullUser
    ? iamUserInfo?.fullUser?.enterprise?.id 
    : !cdksid ?  SPOKE_USERS : null;

    const createOptions = (iamUserInfo:any) => 
      iamUserInfo?.fullUser?.stores?.map((store:any) => ({
        label: `${store.name} - ${store.id}`,
        value: store.id,
        isDefault: store.defaultStore,
      })) || [];
    const findDefaultStore = (options:any) => options.find((store:any) => store.isDefault);
    const options = useMemo(() => {
        const stores = createOptions(iamUserInfo);
        return stores.sort((a:any, b:any) => a.label.localeCompare(b.label));
      }, [iamUserInfo]);

    useEffect(() => {
      if (iamUserInfo) {
        startTransition(() => {
          const defaultStore = findDefaultStore(options);
          if (defaultStore) {
            setSelectedOption(defaultStore);
          }
      });
    }
  }, [iamUserInfo, options]);
    
    const handleDateChange = ({
        endDate,
        startDate
    }: {
        endDate: any,
        startDate: any
    }) => {
        if(endDate?.diff(startDate, 'days') > 6) {
          setRangeError(true);
        }
        else{
          setRangeError(false);
        }
        setEndDate(endDate);
        setStartDate(startDate);
    };


  const updatedEndDateHasError = endDate?.diff(startDate, 'days') > 6;
  const endDateInputProps = {
    enableCustomValidation: true,
    errorMessage: t('pulse.rangelimit'),
    hasError: updatedEndDateHasError,
    'data-testid': 'data-id-end-date'
  };

const storeSelectHandler = (stores: string[], values: string[]) => {
  const uniques = values.filter((item, pos, self) => self.indexOf(item) === pos);
  setSelectedValues(uniques);
}

function getTimeFormat(date:Moment){
  return `T${date.hours()}:${date.minutes()}:${date.seconds()}:${date.milliseconds()}Z`;
}

const onSearchClick = () => {
  trackEvent(AMPLITUDE_EVENTS.UNI_PULSE, {'Action': 'Search'});
  searchHandler({
    startDate: startDate?.format('YYYY-MM-DD') + getTimeFormat(defStartDate.startOf('day')),
    endDate: endDate?.format('YYYY-MM-DD') + getTimeFormat(defEndDate.endOf('day')),
    storeids: selectedValues?.join(','),
    loginid: loginId,
    ip,
    enterpriseId
  })
}
  
  return (
    <>
        <UserLookFormFilters>
            <MultiAutocomplete
                options={options}
                label={t('pulse.filterstore')}
                onSelect={storeSelectHandler}
                placeholder={t('pulse.selectstore')}
                selectedOption={selectedOption} />
            <Input
                dataTestId="pulse-user-input-ip"
                errorMessage=""
                hideBorder="none"
                iconPosition="trailing"
                id="pulse-user-input-ip"
                inputDivider="left"
                label="IP"
                name="pulse-user-input-ip"
                onChange={(evt:any) => {setIp(evt.target.value)}}
                placeholder={t('pulse.enterip')}
                size="standard"
                value={ip}
            />
             <Input
                dataTestId="pulse-user-input"
                errorMessage=""
                hideBorder="none"
                iconPosition="trailing"
                id="pulse-user-input"
                inputDivider="left"
                label={t('pulse.enterlogin')}
                name="pulse-user-input"
                onChange={(evt:any) => {setLoginId(evt.target.value)}}
                placeholder={t('pulse.loginid')}
                size="standard"
                value={loginId}
            />
            <StyledDateRangePicker>
            <DateRangePicker 
                endDate={endDate} 
                endDateInputName="end-date-input-simple" 
                endDateLabel={t('pulse.enddate')}
                id="date-range-picker-simple" 
                onDatesChange={handleDateChange} 
                startDate={startDate} 
                startDateInputName="start-date-input-simple" 
                startDateLabel={t('pulse.startdate')}
                allowDaysInPast={true}
                style={{ 'margin-right': '10px', 'z-index':'10000' }}
                endDateInputProps={endDateInputProps}
                startDateInputProps={{'data-testid': 'data-id-pulse-start-date'}}
                />
              </StyledDateRangePicker>

              <Button
                style={ {'margin-left': '10px'} }
                dataTestId="button-pulse-user-search"
                iconColor="primary"
                iconSize={24}
                onClick={onSearchClick}
                size="standard"
                text={t('pulse.search')}
                variant="primary"
                isDisabled={rangeError}
              />
        </UserLookFormFilters>
    </>
  )
}
